import { useCallback, useEffect, useState } from "react";
import styles from "./Home.module.css";
import SearchComponents from "./Components/SearchComponents";
import { useDispatch, useSelector } from "react-redux";
import {
  handleExtract,
  handleExtractUrl,
  regulation,
  regulationLearnMoreUrl,
  regulationTitle,
} from "../../Components/Helper";
import { fetchAllPosts } from "../../Services/Slices/allPostsSlice";
import { showAppToast } from "../../Components/Toast/showAppToast";
import { getToastTypeForHttpStatus } from "../../Components/Toast/getToastTypeForHttpStatus";
import { fetchPublic } from "../../Services/Slices/publicSlice";
import { usePermission } from "../../Auth/PermissionContext";
import DataTableComponents from "./Components/DataTableComponents";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  useMediaQuery,
} from "@geti/identidade-visual-react";
import { ChevronDown, ExternalLink, FileText } from "lucide-react";

const Home = () => {
  const dispatch = useDispatch();
  const { isAdmin } = usePermission();
  const response = useSelector((state: any) => state.publicSlice);
  const allPostsResponse = useSelector((state: any) => state.allPostsSlice);
  const deletePublishedResponse = useSelector(
    (state: any) => state.deletePublishedFileSlice
  );
  const [extracted, setExtracted] = useState<any>([]);
  const [backup, setBackup] = useState<any>({});
  const [page, setPage] = useState<number>(1);
  const [isSearched, setIsSearched] = useState<boolean>(false);
  const [tempPage, setTempPage] = useState<number | null>(null);
  const [regulationExpanded, setRegulationExpanded] = useState(false);
  const isRegulationCollapsible = useMediaQuery("(max-width: 430px)");

  const columns = [
    { title: "Edição", property: "edition" },
    { title: "Publicação", property: "date" },
    ...(isAdmin
        ? [{ title: "Ações", property: "actions", meta: { className: "w-16" } }]
        : [
            { title: "Visualizar", property: "view" },
            { title: "Arquivo", property: "presigned_url" },
          ]),
  ];

  const handleFiltersChange = useCallback(
    (payload: Record<string, unknown> | null) => {
      if (payload === null) {
        setIsSearched(false);
        setBackup({});
        setPage(1);
        setTempPage(1);
        dispatch<any>(fetchAllPosts("1", false));
        return;
      }

      setIsSearched(true);
      setBackup(payload);
      setPage(1);
      setTempPage(1);
      dispatch<any>(fetchPublic(payload, "1"));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!isSearched && page !== tempPage) {
      dispatch<any>(fetchAllPosts(page.toString(), false));
      setTempPage(page);
    }
  }, [dispatch, isSearched, page, tempPage]);

  useEffect(() => {
    if (isSearched) return;
    dispatch<any>(fetchAllPosts(page.toString(), false));
  }, [deletePublishedResponse.data?.response, dispatch, isSearched, page]);

  useEffect(() => {
    if (!isSearched) return;
    if (page !== tempPage) {
      dispatch<any>(fetchPublic(backup, page.toString()));
      setTempPage(page);
    }
  }, [dispatch, isSearched, backup, page, tempPage]);

  useEffect(() => {
    setExtracted([]);
    if (!isSearched) {
      handleExtract(allPostsResponse.data.results, setExtracted);
    } else {
      const filterPostTypes = Array.isArray(backup.post_type)
        ? backup.post_type
        : [];
      handleExtractUrl(
        response.data.results,
        setExtracted,
        filterPostTypes
      );
    }
  }, [isSearched, response.data, allPostsResponse.data, backup.post_type]);

  useEffect(() => {
    if (deletePublishedResponse.error) {
      showAppToast("deletePublishedError");
    }
  }, [deletePublishedResponse.error]);

  useEffect(() => {
    const apiError = isSearched ? response.error : allPostsResponse.error;
    if (!apiError || apiError === true) return;

    const status = typeof apiError === "object" ? apiError.status : 500;
    showAppToast(getToastTypeForHttpStatus(status));
  }, [isSearched, response.error, allPostsResponse.error]);

  const tableData = extracted;
  const tableTotal = isSearched
    ? response.data.count
    : allPostsResponse.data.count;
  const tableLoading = isSearched
    ? response.loading
    : allPostsResponse.loading;
  const tableIsEmpty =
    (isSearched && response?.data?.results?.length === 0) ||
    (!isSearched && allPostsResponse?.data?.results?.length === 0);
  const tableError = isSearched ? response.error : allPostsResponse.error;

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.tableRow}>
          <div className={styles.tableColumn}>
            <DataTableComponents
              title={"Últimas edições"}
              columns={columns}
              data={tableData}
              setPage={setPage}
              page={page}
              total={tableTotal}
              isEmpty={tableIsEmpty}
              loading={tableLoading}
              error={tableError}
              filtersContent={
                <SearchComponents
                  onFiltersChange={handleFiltersChange}
                  embedded
                />
              }
            />
          </div>
          <aside className={styles.sidebarColumn}>
            <Card className={styles.regulationCard}>
              <CardHeader className={styles.regulationHeader}>
                {isRegulationCollapsible ? (
                  <button
                    type="button"
                    className={styles.regulationToggle}
                    onClick={() => setRegulationExpanded((open) => !open)}
                    aria-expanded={regulationExpanded}
                    aria-controls="regulation-panel"
                  >
                    <div className={styles.regulationTitleRow}>
                      <FileText
                        className={styles.regulationIcon}
                        aria-hidden
                      />
                      <CardTitle className={styles.regulationTitle}>
                        {regulationTitle}
                      </CardTitle>
                      <ChevronDown
                        className={`${styles.regulationChevron} ${
                          regulationExpanded
                            ? styles.regulationChevronOpen
                            : ""
                        }`}
                        aria-hidden
                      />
                    </div>
                  </button>
                ) : (
                  <div className={styles.regulationTitleRow}>
                    <FileText className={styles.regulationIcon} aria-hidden />
                    <CardTitle className={styles.regulationTitle}>
                      {regulationTitle}
                    </CardTitle>
                  </div>
                )}
                <div className={styles.regulationDivider} aria-hidden />
              </CardHeader>
              {(!isRegulationCollapsible || regulationExpanded) && (
                <CardContent
                  id="regulation-panel"
                  className={styles.regulationContent}
                >
                  <p className={styles.regulationText}>{regulation}</p>
                  <Button
                    variant="outline"
                    size="default"
                    nativeButton={false}
                    className={styles.learnMoreButton}
                    render={
                      <a
                        href={regulationLearnMoreUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Saiba mais sobre o Diário Oficial (abre em nova aba)"
                      />
                    }
                  >
                    Saiba mais
                    <ExternalLink
                      className={styles.learnMoreIcon}
                      aria-hidden
                    />
                  </Button>
                </CardContent>
              )}
            </Card>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default Home;
